import React, { useMemo, useState } from "react";
import "./HomeScreen.css";

const reviews = [
  {
    name: "Ethan R.",
    role: "Futures Trader",
    text: "Tradernal made me realize I wasn’t losing because of strategy. I was losing because of execution. The review overview changed that fast.",
  },
  {
    name: "Marcus T.",
    role: "Funded Account Trader",
    text: "The dashboard feels premium, but the real value is seeing my mistakes clearly. It finally feels like I’m reviewing trades like a professional.",
  },
  {
    name: "James S.",
    role: "Intraday Trader",
    text: "I used to take screenshots and notes everywhere and do my journaling on a google doc. Now everything is in one place, and the AI overview helps me catch patterns I kept missing, while proviing improvements for my next trade",
  },
  {
    name: "Martin D.",
    role: "Funded Account Trader",
    text: "This is actually crazy fr. I love the Ai review and how easy everything is to navigate. Would def recomend to any trader. ",
  },
];

const faqs = [
  {
    q: "What do I get on the free Starter plan?",
    a: "Starter gives you the core journal: log trades, track basic PNL, view your calendar, and build consistency. It is designed to let traders start journaling without friction.",
  },
  {
    q: "What unlocks with Pro?",
    a: "Pro unlocks the full Tradernal experience: AI overviews, screenshot-based reviews, deeper insights, smarter feedback, and the tools built to improve execution over time.",
  },
  {
    q: "Do I need to connect a broker?",
    a: "No. Tradernal can work as a manual trading journal first. You can enter trades yourself and keep your review process clean and simple.",
  },
  {
    q: "Can I upload chart screenshots?",
    a: "Yes. Screenshot review is one of the key parts of the Pro experience. It helps turn your journal from simple logging into actual performance review.",
  },
  {
    q: "Is Tradernal only for advanced traders?",
    a: "No. Newer traders can use it to build discipline, while serious traders can use it to tighten execution and review performance at a higher level.",
  },
  {
    q: "Is my data private?",
    a: "Yes. Tradernal is built around personal performance data and journal privacy. Your trades and screenshots are meant to stay yours.",
  },
];

function Metric({ value, label, glow }) {
  return (
    <div className={`tn-metric ${glow ? "tn-metric-glow" : ""}`}>
      <div className="tn-metric-value">{value}</div>
      <div className="tn-metric-label">{label}</div>
    </div>
  );
}

function ReviewCard({ name, role, text }) {
  return (
    <div className="tn-review-card">
      <div className="tn-review-stars">★★★★★</div>
      <p className="tn-review-text">“{text}”</p>
      <div className="tn-review-user">
        <div className="tn-review-avatar">{name.charAt(0)}</div>
        <div>
          <div className="tn-review-name">{name}</div>
          <div className="tn-review-role">{role}</div>
        </div>
      </div>
    </div>
  );
}

function PricingCard({ title, price, subtitle, featured, features, buttonText }) {
  return (
    <div className={`tn-pricing-card ${featured ? "tn-pricing-card-featured" : ""}`}>
      {featured && <div className="tn-badge">Most Popular</div>}
      <div className="tn-pricing-title">{title}</div>
      <div className="tn-pricing-price-row">
        <span className="tn-pricing-price">{price}</span>
        {price !== "Free" && <span className="tn-pricing-period">/month</span>}
      </div>
      <div className="tn-pricing-subtitle">{subtitle}</div>

      <div className="tn-pricing-features">
        {features.map((feature) => (
          <div key={feature} className="tn-pricing-feature">
            <span className="tn-check">✓</span>
            <span>{feature}</span>
          </div>
        ))}
      </div>

      <a href="#final-cta" className={`tn-pricing-button ${featured ? "tn-pricing-button-featured" : ""}`}>
        {buttonText}
      </a>
    </div>
  );
}

function FaqItem({ q, a, open, onClick }) {
  return (
    <div className={`tn-faq-item ${open ? "open" : ""}`} onClick={onClick}>
      <div className="tn-faq-question">
        <span>{q}</span>
        <span className="tn-faq-icon">{open ? "−" : "+"}</span>
      </div>
      <div className="tn-faq-answer">{a}</div>
    </div>
  );
}

function HomeScreen() {
  const [openFaq, setOpenFaq] = useState(0);

  const chartBars = useMemo(
    () => [24, 34, 28, 41, 38, 55, 49, 63, 57, 74, 68, 88],
    []
  );

  return (
    <div className="tn-home">
      <div className="tn-bg-grid"></div>
      <div className="tn-orb tn-orb-one"></div>
      <div className="tn-orb tn-orb-two"></div>
      <div className="tn-noise"></div>

      <header className="tn-navbar">
        <div className="tn-brand">
          <div className="tn-brand-mark">
            <div className="tn-brand-square"></div>
            <div className="tn-brand-arrow"></div>
            <div className="tn-brand-stick tn-brand-stick-a"></div>
            <div className="tn-brand-stick tn-brand-stick-b"></div>
            <div className="tn-brand-stick tn-brand-stick-c"></div>
          </div>
          <div className="tn-brand-text">Tradernal</div>
        </div>

        <nav className="tn-nav-links">
          <a href="#features">Features</a>
          <a href="#reviews">Reviews</a>
          <a href="#pricing">Pricing</a>
          <a href="#faq">FAQ</a>
        </nav>

        <div className="tn-nav-actions">
          <a href="/dashboard" className="tn-nav-login">Sign In</a>
          <a href="/dashboard" className="tn-nav-cta">Start Now</a>
        </div>
      </header>

      <main>
        <section className="tn-hero">
          <div className="tn-announcement">
            Built for serious traders who want sharper reviews, cleaner data, and better execution, with our advanced AI.
          </div>

          <h1 className="tn-hero-title">
            Journal smarter.
            <span> Trade better.</span>
          </h1>

          <p className="tn-hero-subtitle">
            Tradernal is the premium trading journal for disciplined traders. Log trades, review
            screenshots, track performance, and unlock AI-powered insights that help you improve
            over time.
          </p>

          <div className="tn-hero-actions">
            <a href="/dashboard" className="tn-primary-btn">Start Now</a>
            <a href="#pricing" className="tn-secondary-btn">See Pricing</a>
          </div>

          <div className="tn-hero-proof">
            <span>Starter is free</span>
            <span>•</span>
            <span>Pro unlocks AI review</span>
            <span>•</span>
            <span>Built for consistency and growth</span>
          </div>

          <div className="tn-hero-metrics">
            <Metric value="4.9/5" label="Trader rating" />
            <Metric value="AI Review" label="Built into Pro" glow />
            <Metric value="Fast Journal" label="Log trades in seconds" />
          </div>
        </section>

        <section className="tn-showcase">
          <div className="tn-showcase-shell">
            <div className="tn-showcase-topbar">
              <div className="tn-topbar-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <div className="tn-topbar-url">tradernal.app</div>
              <div className="tn-topbar-pill">Live Dashboard</div>
            </div>

            <div className="tn-showcase-content">
              <aside className="tn-showcase-sidebar">
                <div className="tn-showcase-logo">Tradernal</div>
                <div className="tn-showcase-link active">Dashboard</div>
                <div className="tn-showcase-link">Trades</div>
                <div className="tn-showcase-link">Calendar</div>
                <div className="tn-showcase-link">AI Review</div>
                <div className="tn-showcase-link">Insights</div>
              </aside>

              <div className="tn-showcase-main">
                <div className="tn-showcase-header">
                  <div>
                    <div className="tn-showcase-title">Performance Dashboard</div>
                    <div className="tn-showcase-subtitle">
                      Review your execution, patterns, and performance at a glance.
                    </div>
                  </div>
                  <button className="tn-showcase-button">+ Add Trade</button>
                </div>

                <div className="tn-stats-grid">
                  <div className="tn-stat-card">
                    <div className="tn-stat-label">Total PNL</div>
                    <div className="tn-stat-value gain">+$2,250</div>
                    <div className="tn-stat-sub">5 total trades</div>
                  </div>
                  <div className="tn-stat-card">
                    <div className="tn-stat-label">Win Rate</div>
                    <div className="tn-stat-value gain">80%</div>
                    <div className="tn-stat-sub">4 wins · 1 loss</div>
                  </div>
                  <div className="tn-stat-card">
                    <div className="tn-stat-label">Trader Momentum</div>
                    <div className="tn-stat-value">88</div>
                    <div className="tn-stat-sub">Live score</div>
                  </div>
                </div>

                <div className="tn-insight-panel">
                  <div className="tn-insight-head">
                    <div>
                      <div className="tn-insight-kicker">Daily AI Overview</div>
                      <div className="tn-insight-title">The edge starts after the trade.</div>
                    </div>
                    <div className="tn-score-ring">
                      <span>91</span>
                    </div>
                  </div>

                  <div className="tn-insight-body">
                    <div className="tn-insight-copy">
                      <div className="tn-insight-line">
                        <strong>Strength:</strong> Clean journaling and strong patience on winners.
                      </div>
                      <div className="tn-insight-line">
                        <strong>Weakness:</strong> Slight hesitation on entries after confirmation.
                      </div>
                      <div className="tn-insight-line">
                        <strong>Next Step:</strong> Keep screenshot reviews attached to every setup.
                      </div>
                    </div>

                    <div className="tn-mini-chart">
                      {chartBars.map((height, index) => (
                        <div
                          key={index}
                          className="tn-mini-bar"
                          style={{ height: `${height}%`, animationDelay: `${index * 0.08}s` }}
                        ></div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="tn-calendar-strip">
                  <div className="tn-day neutral">No trades</div>
                  <div className="tn-day neutral">No trades</div>
                  <div className="tn-day loss">-$250</div>
                  <div className="tn-day gain">+$2300</div>
                  <div className="tn-day neutral">No trades</div>
                  <div className="tn-day weekly">Weekly +$2050</div>
                </div>
              </div>
            </div>
          </div>

          <div className="tn-floating-card tn-floating-a">
            <div className="tn-floating-label">Review Quality</div>
            <div className="tn-floating-value">Elite</div>
          </div>

          <div className="tn-floating-card tn-floating-b">
            <div className="tn-floating-label">AI Insight</div>
            <div className="tn-floating-value">Live</div>
          </div>
        </section>

        <section className="tn-section" id="features">
          <div className="tn-section-head">
            <div className="tn-section-kicker">Why Tradernal</div>
            <h2>Built to make disciplined traders even sharper.</h2>
            <p>
              This is more than a place to store trades. It is a performance system designed
              to help serious traders review better, learn faster, and execute cleaner.
            </p>
          </div>

          <div className="tn-feature-grid">
            <div className="tn-feature-card">
              <div className="tn-feature-icon">01</div>
              <h3>Fast trade journaling</h3>
              <p>
                Log entries, exits, setup notes, and outcomes in a clean workflow designed for speed.
              </p>
            </div>

            <div className="tn-feature-card">
              <div className="tn-feature-icon">02</div>
              <h3>Screenshot-based review</h3>
              <p>
                Attach chart screenshots so each trade has visual context, not just numbers.
              </p>
            </div>

            <div className="tn-feature-card">
              <div className="tn-feature-icon">03</div>
              <h3>AI daily overview</h3>
              <p>
                Turn raw journaling into clear feedback with insights on patterns, strengths, and mistakes.
              </p>
            </div>

            <div className="tn-feature-card">
              <div className="tn-feature-icon">04</div>
              <h3>Performance analytics</h3>
              <p>
                Track PNL, win rate, momentum, and execution trends across your calendar and dashboard.
              </p>
            </div>
          </div>
        </section>

        <section className="tn-section tn-how">
          <div className="tn-section-head">
            <div className="tn-section-kicker">How it works</div>
            <h2>Three steps. Real improvement.</h2>
          </div>

          <div className="tn-steps">
            <div className="tn-step">
              <div className="tn-step-number">1</div>
              <h3>Journal</h3>
              <p>Enter trades cleanly and keep every setup organized in one place.</p>
            </div>
            <div className="tn-step">
              <div className="tn-step-number">2</div>
              <h3>Review</h3>
              <p>Upload screenshots and revisit your decisions with structure instead of emotion.</p>
            </div>
            <div className="tn-step">
              <div className="tn-step-number">3</div>
              <h3>Improve</h3>
              <p>Use analytics and AI feedback to find what is helping you win and what is holding you back.</p>
            </div>
          </div>
        </section>

        <section className="tn-section" id="reviews">
          <div className="tn-section-head">
            <div className="tn-section-kicker">Trusted by traders</div>
            <h2>What users would say after switching to Tradernal.</h2>
          </div>

          <div className="tn-reviews-grid">
            {reviews.map((review) => (
              <ReviewCard key={review.name} {...review} />
            ))}
          </div>
        </section>

        <section className="tn-section" id="pricing">
          <div className="tn-section-head">
            <div className="tn-section-kicker">Pricing</div>
            <h2>Start Now. Upgrade when you want the full edge.</h2>
            <p>
              Starter lets traders build the journaling habit. Pro unlocks the premium review system,
              AI insights, and the tools designed to accelerate growth.
            </p>
          </div>

          <div className="tn-pricing-grid">
            <PricingCard
              title="Starter"
              price="Free"
              subtitle="For traders getting started with journaling."
              buttonText="Start Free"
              features={[
                "Manual trade entry",
                "Basic PNL tracking",
                "Calendar view",
                "Simple stats",
                "Core journaling workflow",
              ]}
            />

            <PricingCard
              title="Pro"
              price="$29"
              subtitle="For serious traders who want the full Tradernal edge."
              featured
              buttonText="Upgrade to Pro"
              features={[
                "Everything in Starter",
                "AI daily overview",
                "AI weekly overview",
                "Screenshot review",
                "Advanced insights",
                "Advanced improvement advice",
                "Deeper performance tracking",
                "Premium review experience",
              ]}
            />
          </div>
        </section>

        <section className="tn-section" id="faq">
          <div className="tn-section-head">
            <div className="tn-section-kicker">FAQ</div>
            <h2>Questions traders usually ask before they start.</h2>
          </div>

          <div className="tn-faq">
            {faqs.map((item, index) => (
              <FaqItem
                key={item.q}
                q={item.q}
                a={item.a}
                open={openFaq === index}
                onClick={() => setOpenFaq(openFaq === index ? -1 : index)}
              />
            ))}
          </div>
        </section>

        <section className="tn-final-cta" id="final-cta">
          <div className="tn-final-inner">
            <div className="tn-final-kicker">Tradernal Pro</div>
            <h2>The premium journal for traders who are serious about improvement.</h2>
            <p>
              Start free, build consistency, and upgrade when you are ready for AI-powered review,
              better data, and sharper execution.
            </p>
            <div className="tn-final-actions">
              <a href="/dashboard" className="tn-primary-btn">Start Now</a>
              <a href="#pricing" className="tn-secondary-btn">View Pricing</a>
            </div>
          </div>
        </section>
      </main>

      <footer className="tn-footer">
        <div className="tn-footer-brand">Tradernal</div>
        <div className="tn-footer-links">
          <a href="#features">Features</a>
          <a href="#pricing">Pricing</a>
          <a href="#faq">FAQ</a>
        </div>
        <div className="tn-footer-copy">© 2026 Tradernal. Built for disciplined traders.</div>
      </footer>
    </div>
  );
}

export default HomeScreen;